/**
 * T-26: Offline Status Component
 * 
 * Displays network status and offline capabilities to users
 */

import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, CloudOff, Cloud, AlertCircle, CheckCircle } from 'lucide-react';
import { getCacheStatistics, formatCacheSize } from '../lib/offlineScenes';

interface OfflineStatusProps {
  className?: string;
  showDetails?: boolean;
  position?: 'top' | 'bottom';
}

interface NetworkStatus {
  isOnline: boolean;
  hasOfflineCapability: boolean;
  cachedScenesCount: number;
  cacheSize: number;
  lastOfflineAt?: string;
}

export const OfflineStatus: React.FC<OfflineStatusProps> = ({
  className = '',
  showDetails = false,
  position = 'top'
}) => {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
    hasOfflineCapability: false,
    cachedScenesCount: 0,
    cacheSize: 0
  });
  const [showTooltip, setShowTooltip] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Initial status check
    updateNetworkStatus();

    // Network event listeners
    const handleOnline = () => {
      console.log('[Offline] Network back online');
      updateNetworkStatus();
      
      // Show brief online notification
      setIsVisible(true);
      setTimeout(() => setIsVisible(false), 3000);
    };

    const handleOffline = () => {
      console.log('[Offline] Network went offline');
      updateNetworkStatus();
      setIsVisible(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check cache status periodically
    const cacheInterval = setInterval(updateNetworkStatus, 30000); // Every 30 seconds

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(cacheInterval);
    };
  }, []);

  const updateNetworkStatus = async () => {
    try {
      const stats = await getCacheStatistics();
      const hasServiceWorker = 'serviceWorker' in navigator && 
                             await navigator.serviceWorker.getRegistration();

      setNetworkStatus({
        isOnline: navigator.onLine,
        hasOfflineCapability: hasServiceWorker !== undefined,
        cachedScenesCount: stats.totalScenes,
        cacheSize: stats.totalSize,
        lastOfflineAt: !navigator.onLine ? new Date().toISOString() : undefined
      });
    } catch (error) {
      console.warn('[Offline] Failed to update network status:', error);
    }
  };

  const getStatusIcon = () => {
    if (networkStatus.isOnline) {
      return networkStatus.hasOfflineCapability ? 
        <Cloud className="text-green-600 dark:text-green-400" size={16} /> :
        <Wifi className="text-blue-600 dark:text-blue-400" size={16} />;
    } else {
      return networkStatus.hasOfflineCapability ?
        <CloudOff className="text-orange-600 dark:text-orange-400" size={16} /> :
        <WifiOff className="text-red-600 dark:text-red-400" size={16} />;
    }
  };

  const getStatusText = () => {
    if (networkStatus.isOnline) {
      return networkStatus.hasOfflineCapability ? 'Online (Offline Ready)' : 'Online';
    } else {
      return networkStatus.hasOfflineCapability ? 'Offline Mode' : 'Offline (Limited)';
    }
  };

  const getStatusColor = () => {
    if (networkStatus.isOnline) {
      return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200';
    } else if (networkStatus.hasOfflineCapability) {
      return 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 text-orange-800 dark:text-orange-200';
    } else {
      return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200';
    }
  };

  // Don't show when online unless explicitly requested or there's cached content
  if (networkStatus.isOnline && !showDetails && !isVisible && networkStatus.cachedScenesCount === 0) {
    return null;
  }

  const positionClasses = position === 'top' 
    ? 'top-4 left-1/2 transform -translate-x-1/2'
    : 'bottom-4 left-1/2 transform -translate-x-1/2';

  return (
    <div className={`fixed ${positionClasses} z-40 ${className}`}>
      <div
        className={`px-4 py-2 rounded-lg border shadow-lg backdrop-blur-sm transition-all duration-300 ${getStatusColor()} ${
          isVisible || !networkStatus.isOnline || showDetails ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'
        }`}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <div className="flex items-center gap-2 text-sm font-medium">
          {getStatusIcon()}
          <span>{getStatusText()}</span>
          
          {networkStatus.cachedScenesCount > 0 && (
            <span className="ml-2 px-2 py-1 text-xs bg-white/20 rounded-full">
              {networkStatus.cachedScenesCount} cached
            </span>
          )}
        </div>

        {/* Detailed tooltip */}
        {showTooltip && (showDetails || networkStatus.cachedScenesCount > 0) && (
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 p-3 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 min-w-64 z-50">
            <div className="space-y-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="font-medium">Network Status:</span>
                <span className={networkStatus.isOnline ? 'text-green-600' : 'text-red-600'}>
                  {networkStatus.isOnline ? 'Connected' : 'Disconnected'}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="font-medium">Offline Ready:</span>
                <span className={networkStatus.hasOfflineCapability ? 'text-green-600' : 'text-red-600'}>
                  {networkStatus.hasOfflineCapability ? 'Yes' : 'No'}
                </span>
              </div>

              {networkStatus.cachedScenesCount > 0 && (
                <>
                  <hr className="border-gray-200 dark:border-gray-600" />
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Cached Scenes:</span>
                    <span>{networkStatus.cachedScenesCount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Cache Size:</span>
                    <span>{formatCacheSize(networkStatus.cacheSize)}</span>
                  </div>
                </>
              )}

              {!networkStatus.isOnline && (
                <>
                  <hr className="border-gray-200 dark:border-gray-600" />
                  <div className="flex items-start gap-2">
                    <AlertCircle className="text-orange-500 mt-0.5" size={12} />
                    <span className="text-gray-600 dark:text-gray-400">
                      {networkStatus.hasOfflineCapability
                        ? 'You can continue working with cached content and local features.'
                        : 'Limited functionality available while offline.'}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Compact status indicator for header/toolbar
 */
export const OfflineIndicator: React.FC<{
  onClick?: () => void;
  className?: string;
}> = ({ onClick, className = '' }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [hasOfflineSupport, setHasOfflineSupport] = useState(false);

  useEffect(() => {
    const updateStatus = () => {
      setIsOnline(navigator.onLine);
    };

    const checkOfflineSupport = async () => {
      const registration = await navigator.serviceWorker?.getRegistration();
      setHasOfflineSupport(!!registration);
    };

    window.addEventListener('online', updateStatus);
    window.addEventListener('offline', updateStatus);
    checkOfflineSupport();

    return () => {
      window.removeEventListener('online', updateStatus);
      window.removeEventListener('offline', updateStatus);
    };
  }, []);

  const getIcon = () => {
    if (isOnline) {
      return hasOfflineSupport ? 
        <Cloud size={18} className="text-green-600 dark:text-green-400" /> :
        <Wifi size={18} className="text-blue-600 dark:text-blue-400" />;
    } else {
      return hasOfflineSupport ?
        <CloudOff size={18} className="text-orange-600 dark:text-orange-400" /> :
        <WifiOff size={18} className="text-red-600 dark:text-red-400" />;
    }
  };

  return (
    <button
      onClick={onClick}
      className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${className}`}
      title={isOnline ? 'Online' : 'Offline'}
      aria-label={`Network status: ${isOnline ? 'Online' : 'Offline'}`}
    >
      {getIcon()}
    </button>
  );
};

/**
 * Cache status widget for settings/debug
 */
export const CacheStatusWidget: React.FC = () => {
  const [stats, setStats] = useState({
    totalScenes: 0,
    totalSize: 0,
    oldestScene: null as string | null,
    newestScene: null as string | null,
    lastAccessed: null as string | null
  });

  useEffect(() => {
    const updateStats = async () => {
      try {
        const cacheStats = await getCacheStatistics();
        setStats(cacheStats);
      } catch (error) {
        console.warn('[Offline] Failed to load cache stats:', error);
      }
    };

    updateStats();
    const interval = setInterval(updateStats, 10000); // Update every 10 seconds

    return () => clearInterval(interval);
  }, []);

  if (stats.totalScenes === 0) {
    return (
      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
          <CloudOff size={20} />
          <span>No cached scenes</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
      <div className="flex items-center gap-2 mb-3">
        <CheckCircle className="text-blue-600 dark:text-blue-400" size={20} />
        <span className="font-medium text-blue-900 dark:text-blue-100">Offline Cache</span>
      </div>
      
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <span className="text-gray-600 dark:text-gray-400">Scenes:</span>
          <span className="ml-2 font-medium">{stats.totalScenes}</span>
        </div>
        <div>
          <span className="text-gray-600 dark:text-gray-400">Size:</span>
          <span className="ml-2 font-medium">{formatCacheSize(stats.totalSize)}</span>
        </div>
      </div>

      {stats.lastAccessed && (
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          Last accessed: {new Date(stats.lastAccessed).toLocaleString()}
        </div>
      )}
    </div>
  );
};